import { useState, type ReactNode } from "react";
import { Ban, Check, Pencil, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { OrgUnitPlayButton } from "@/components/agents/configuration-server/OrgUnitPlayButton";
import { OrgUnitStatusBadge } from "@/components/agents/configuration-server/OrgUnitStatusBadge";
import { canEditOrgUnit, hasOrgUnitActions } from "@/components/agents/configuration-server/orgUnitState";
import { RowActionButton } from "@/components/brand/RowActionButton";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  effectiveOrgId,
  executionMigrationStatus,
  type MigrationStatus,
  type OrgTreeNode,
  type ScopeVerdict,
} from "@/lib/org-structure-tree";
import { cn } from "@/lib/utils";

// Tentou e não chegou ao CBC: o selo fica ao lado do play, que passa a servir
// de "tentar de novo". Aguardando decisão também tem selo, mesmo sem play: sem
// ele a linha ficava muda numa unidade que está esperando a pessoa. Com
// "Running" e "Completed", são os selos da linha — todos de execução; o escopo
// se lê pelas ações disponíveis.
const ATTEMPTED_NOT_REGISTERED: ReadonlySet<MigrationStatus> = new Set([
  "failed",
  "interrupted",
  "awaiting_human_decision",
]);

/**
 * Ações de gerenciamento e execução de uma unidade, na própria linha/card.
 *
 * O que aparece depende do estado — a regra de negócio da #845 é a ordem
 * "decidir o escopo, depois executar", então o play só existe para unidade
 * aprovada, e unidade já cadastrada no CBC não tem mais ação nenhuma: tirar
 * do escopo não desfaria o cadastro (o backend recusa com 409), e o que
 * sobra é o status. Quando o lápis aparece é regra única de `canEditOrgUnit`
 * (spec 2026-10-26, D24); excluir, só a manual não aprovada.
 *
 * `dispatching` é o intervalo entre o clique no play e o status "running"
 * chegar do servidor: o play já gira e trava, para ninguém disparar de novo.
 *
 * Todas as ações — play inclusive — são `RowActionButton`, o mesmo formato
 * do "ver detalhes" da linha; lista e card usam exatamente os mesmos botões.
 * `labeled` é o rodapé do painel de detalhes: as mesmas ações, pela mesma
 * regra de estado, com o nome por extenso — ali há espaço para ele.
 */
export function OrgUnitRowActions({
  node,
  labeled = false,
  cbcConnected,
  runInProgressLabel,
  runBlockedReason,
  dispatching = false,
  hideExecution = false,
  hideCrud = false,
  onRun,
  onRequestVerdict,
  onEdit,
  onDelete,
}: {
  node: OrgTreeNode;
  labeled?: boolean;
  cbcConnected: boolean;
  runInProgressLabel?: string;
  /** Etapa 3: outra execução no projeto (extração, cadastro no CBC ou conversa) trava o play com este motivo. */
  runBlockedReason?: string | null;
  dispatching?: boolean;
  hideExecution?: boolean;
  /** Execução (etapa 3): sem editar nem excluir a unidade. */
  hideCrud?: boolean;
  onRun?: (node: OrgTreeNode) => void;
  /** Sem ele, os botões de veredito (✓ aprovar e ⊘ tirar do escopo) não aparecem. */
  onRequestVerdict?: (node: OrgTreeNode, verdict: Exclude<ScopeVerdict, "pending">) => void;
  onEdit: (node: OrgTreeNode) => void;
  onDelete: (node: OrgTreeNode) => void;
}) {
  const { t } = useTranslation();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const fromDiagnostic = node.origin !== "manual";

  // No rodapé do painel o estado já está no cabeçalho (selo) e na seção
  // Scope; sem ação nenhuma a oferecer, o rodapé simplesmente não existe.
  if (labeled && !hasOrgUnitActions(node)) {
    return null;
  }

  if (node.migrationStatus === "completed") {
    return (
      <div className="flex shrink-0 flex-wrap items-center gap-1.5">
        <OrgUnitStatusBadge status="completed" />
      </div>
    );
  }

  if (!hideExecution && onRun && (node.migrationStatus === "running" || dispatching)) {
    return (
      <div className={cn("flex shrink-0 items-center gap-1.5", labeled && "justify-end")}>
        {node.migrationStatus === "running" && !labeled && (
          <OrgUnitStatusBadge status="running" />
        )}
        <OrgUnitPlayButton status="running" labeled={labeled} onRun={() => {}} />
      </div>
    );
  }

  const runTitle = !cbcConnected
    ? t("CBC connection required to run the agent")
    : runInProgressLabel
      ? t(
          "A registration is already running for this project ({{label}}) — wait for it to finish.",
          { label: runInProgressLabel },
        )
      : runBlockedReason ?? undefined;

  function action(
    label: string,
    icon: ReactNode,
    onClick: () => void,
    tone: "neutral" | "danger" | "approve" | "reject" = "neutral",
  ) {
    if (labeled) {
      return (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onClick}
          className={cn(
            tone === "danger" && "text-[#d4353c] hover:text-[#b02a31]",
            // Mesmas cores das ações da tabela: aprovar verde, tirar do escopo vermelho.
            tone === "approve" && "border-[rgba(39,161,85,0.25)] bg-[rgba(39,161,85,0.08)] text-[#1e7d42] hover:border-[var(--exed-success)] hover:bg-[var(--exed-success)] hover:text-white",
            tone === "reject" && "border-[rgba(212,53,60,0.22)] bg-[rgba(212,53,60,0.06)] text-[#a3262c] hover:border-[var(--exed-danger)] hover:bg-[var(--exed-danger)] hover:text-white",
          )}
        >
          {icon}
          {label}
        </Button>
      );
    }
    return <RowActionButton label={label} icon={icon} tone={tone} onClick={onClick} />;
  }

  return (
    <div
      data-org-key={node.key}
      className={cn(
        "flex shrink-0 flex-wrap items-center",
        // No rodapé do painel (nome por extenso) os botões vão para a direita.
        labeled ? "justify-end gap-2" : "gap-1.5",
      )}
    >
      {/* No painel, o status já está no selo do cabeçalho. */}
      {!labeled && ATTEMPTED_NOT_REGISTERED.has(node.migrationStatus) && (
        <OrgUnitStatusBadge status={node.migrationStatus} />
      )}
      {/* Aguardando decisão: o SAP recusou o ID e a pessoa responde no terminal
          da etapa 3 (sim, não ou outro ID). Executar de novo seria um segundo caminho para
          a mesma pergunta; "Retry" fica para quando algo deu errado. */}
      {onRun && node.scopeVerdict === "approved" && node.migrationStatus !== "awaiting_human_decision" && (
        <OrgUnitPlayButton
          status={node.migrationStatus}
          labeled={labeled}
          disabled={!cbcConnected || Boolean(runInProgressLabel) || Boolean(runBlockedReason)}
          title={runTitle}
          onRun={() => onRun(node)}
        />
      )}
      {!fromDiagnostic && onRequestVerdict && node.scopeVerdict !== "approved" &&
        action(t("Approve"), <Check />, () => onRequestVerdict(node, "approved"), "approve")}
      {!fromDiagnostic && onRequestVerdict && node.scopeVerdict !== "out_of_scope" &&
        action(t("Take out of scope"), <Ban />, () => onRequestVerdict(node, "out_of_scope"), "reject")}
      {!hideCrud && canEditOrgUnit({ ...node, migrationStatus: executionMigrationStatus(node) }) && action(t("Edit"), <Pencil />, () => onEdit(node))}
      {!hideCrud &&
        node.scopeVerdict !== "approved" &&
        !fromDiagnostic &&
        action(t("Delete"), <Trash2 />, () => setDeleteConfirmOpen(true), "danger")}

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("Delete {{unit}}?", { unit: effectiveOrgId(node) })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("This manually created unit will be permanently removed. This cannot be undone.")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("Cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => onDelete(node)}>{t("Delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
